import { useMemo, useState } from "react"

export type PendingMember = {
  id: string
  seedbedId: string
  groupId?: string | null
  name: string
  email: string
  reconciledUserId?: string | null
  reconciledAt?: string | null
  createdAt: string
  seedbed?: { id: string; name: string } | null
  group?: { id: string; name: string } | null
  reconciledUser?: { id: string; name: string; email: string } | null
}

export type ReconcileCandidate = { id: string; name: string; email: string }

export type PendingMemberReconcileRowProps = {
  member: PendingMember
  candidates: ReconcileCandidate[]
  onReconcile: (memberId: string, userId: string) => Promise<void> | void
  showSeedbed?: boolean
}

export function PendingMemberReconcileRow({ member, candidates, onReconcile, showSeedbed }: PendingMemberReconcileRowProps) {
  const [search, setSearch] = useState("")
  const [linking, setLinking] = useState(false)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return []
    return candidates
      .filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q))
      .slice(0, 6)
  }, [search, candidates])

  async function handlePick(userId: string) {
    setLinking(true)
    try {
      await onReconcile(member.id, userId)
      setSearch("")
    } finally {
      setLinking(false)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{member.name}</p>
          <p className="text-xs text-gray-500">{member.email}</p>
          {showSeedbed && member.seedbed && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {member.seedbed.name}
              {member.group ? ` · ${member.group.name}` : ""}
            </p>
          )}
        </div>

        {member.reconciledUserId ? (
          <span className="rounded-full bg-green-50 px-2 py-1 text-[11px] font-semibold text-green-700 dark:bg-green-900/20 dark:text-green-400">
            Reconciliado ✓{member.reconciledAt ? ` (${new Date(member.reconciledAt).toLocaleDateString("es-CO")})` : ""}
          </span>
        ) : (
          <span className="rounded-full bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
            Pendiente de cuenta
          </span>
        )}
      </div>

      {!member.reconciledUserId && (
        <div className="mt-2 relative">
          <label htmlFor={`reconcile-search-${member.id}`} className="sr-only">
            Vincular a una cuenta existente (buscar por nombre o email)
          </label>
          <input
            id={`reconcile-search-${member.id}`}
            type="text"
            placeholder="Vincular a una cuenta existente (buscar por nombre o email)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={linking}
            className="min-h-11 w-full rounded-lg border border-gray-300 px-3 text-xs dark:border-gray-700 dark:bg-gray-900"
          />
          {linking && (
            <p role="status" aria-live="polite" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Vinculando...
            </p>
          )}
          {filtered.length > 0 && (
            <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
              {filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  disabled={linking}
                  onClick={() => handlePick(c.id)}
                  className="flex min-h-11 w-full flex-col items-start justify-center px-3 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <span className="font-medium text-gray-800 dark:text-white">{c.name}</span>
                  <span className="text-gray-400">{c.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PendingMemberReconcileRow
